"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  title?: string;
}

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  size?: "xs" | "sm" | "md";
  className?: string;
  "aria-label": string;
}

/**
 * A compact radio-group rendered as a pill with a sliding highlight.
 * Used for AND/OR matching and the Popular view toggle.
 */
function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = "sm",
  className,
  ...rest
}: SegmentedProps<T>) {
  const sizeClass =
    size === "xs"
      ? "h-6 text-[11px] [&>button]:px-2"
      : size === "sm"
        ? "h-8 text-[12.5px] [&>button]:px-3"
        : "h-9 text-sm [&>button]:px-3.5";

  return (
    <div
      role="radiogroup"
      aria-label={rest["aria-label"]}
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border border-line bg-surface-1 p-0.5",
        sizeClass,
        className
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cn(
              "inline-flex h-full items-center justify-center gap-1 rounded-full font-medium leading-none whitespace-nowrap transition-colors duration-150",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
              active
                ? "bg-accent-tint-strong text-ink shadow-[0_0_0_1px_rgb(167_139_250/0.35)]"
                : "text-ink-3 hover:text-ink-2"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export { Segmented };
